import type { ReactNode } from 'react'

import type { AlertItem, EngineState, HealthProbe } from '@/lib/types'
import { Spinner } from '@/components/common/Spinner'
import { AlertIcon, CloseIcon, ShieldIcon } from '@/components/common/icons'

interface BannersProps {
  engine: EngineState
  alerts: AlertItem[]
  onDismissAlert: (code: string) => void
  onResume: () => void
  resumePending: boolean
}

type Tone = 'warn' | 'crit' | 'info'

/** Health-пробы, чьё состояние показываем баннером (наблюдаемость, §1.13). */
const HEALTH_BANNER_PROBES = new Set([
  'clock_drift',
  'exchange_health',
  'liquidation',
  'margin',
  'trading_status',
])

/** Коды алертов, уже покрытые health-баннерами/флагами — не дублируем их карточкой алерта. */
const HEALTH_COVERED_CODES = new Set([
  'clock_drift',
  'exchange_down',
  'near_liquidation',
  'resource_pressure',
  'storage_failure',
  'watchdog',
  'safe_startup',
])

function Banner({
  tone,
  icon,
  children,
  action,
}: {
  tone: Tone
  icon: ReactNode
  children: ReactNode
  action?: ReactNode
}) {
  return (
    <div className={`banner banner--${tone}`}>
      <span className="banner__icon">{icon}</span>
      <div className="banner__body">{children}</div>
      {action ? <div className="banner__action">{action}</div> : null}
    </div>
  )
}

export function Banners({ engine, alerts, onDismissAlert, onResume, resumePending }: BannersProps) {
  const items: ReactNode[] = []

  // 1. Safe-startup — входы заблокированы до возобновления оператором (§Этап 15).
  if (engine.safe_startup) {
    items.push(
      <Banner
        key="safe"
        tone="warn"
        icon={<ShieldIcon size={18} />}
        action={
          <button type="button" className="btn btn--sm" onClick={onResume} disabled={resumePending}>
            {resumePending ? <Spinner size={13} className="btn__spin" /> : null}
            Возобновить
          </button>
        }
      >
        <strong>Безопасный старт.</strong> Обнаружена недостоверность состояния при запуске —
        новые входы заблокированы. После проверки нажмите «Возобновить» для повторной сверки.
      </Banner>,
    )
  }

  // 2. STUCK_DIRECTIONAL — потеря хеджа, открытие новых сделок остановлено по всему боту.
  const stuck = engine.active_trades.filter((t) => t.status === 'stuck_directional')
  if (stuck.length > 0) {
    items.push(
      <Banner key="stuck" tone="crit" icon={<AlertIcon size={18} />}>
        <strong>Потеря хеджа (STUCK_DIRECTIONAL):</strong> {stuck.map((t) => t.coin).join(', ')}.
        Открытие новых сделок остановлено до нейтрализации.
      </Banner>,
    )
  }

  // 2b. Halt открытия по всему боту НЕ из-за STUCK (упавший владелец связки / отказ персиста):
  // running/entry_enabled могут быть true, но входов не будет — показываем причину явно (item 6).
  // STUCK уже покрыт баннером выше — не дублируем.
  if (engine.halted && stuck.length === 0) {
    items.push(
      <Banner key="halt" tone="crit" icon={<AlertIcon size={18} />}>
        <strong>Открытие новых сделок остановлено.</strong>{' '}
        {engine.halt_reason ?? 'Инцидент надзора — требуется проверка оператором.'} Открытые
        связки продолжают обслуживаться; для снятия halt — перезапуск движка (повторная сверка).
      </Banner>,
    )
  }

  // 3. Health-блок входов (watchdog/ресурсы/диск, §1.9 пороговое действие).
  if (engine.health?.entry_blocked) {
    items.push(
      <Banner key="hblock" tone="crit" icon={<AlertIcon size={18} />}>
        <strong>Входы заблокированы health-монитором</strong> (зависший надзор / нехватка
        ресурсов или места). Открытие новых сделок приостановлено.
      </Banner>,
    )
  }

  // 4. Наблюдаемость по пробам здоровья (clock-drift/биржи/ликвидация/маржа/торговый статус).
  for (const probe of engine.health?.probes ?? []) {
    if (probe.status === 'ok' || !HEALTH_BANNER_PROBES.has(probe.name)) continue
    items.push(
      <Banner
        key={`probe-${probe.name}`}
        tone={probe.status === 'critical' ? 'crit' : 'warn'}
        icon={<AlertIcon size={18} />}
      >
        <strong>{healthProbeTitle(probe)}:</strong> {probe.detail}
      </Banner>,
    )
  }

  // 5. Прочие оповещения движка (FEE_DRIFT / DAILY_LOSS_LIMIT / HEDGE_LOST / …) — с закрытием.
  for (const alert of alerts) {
    if (alert.severity < 20) continue // только warning+ как баннер
    if (HEALTH_COVERED_CODES.has(alert.code.toLowerCase())) continue
    items.push(
      <Banner
        key={`alert-${alert.code}`}
        tone={alert.severity >= 30 ? 'crit' : 'warn'}
        icon={<AlertIcon size={18} />}
        action={
          <button
            type="button"
            className="icon-btn icon-btn--sm"
            onClick={() => onDismissAlert(alert.code)}
            title="Скрыть"
          >
            <CloseIcon size={15} />
          </button>
        }
      >
        <strong>{alert.code.toUpperCase()}.</strong> {alert.message}
      </Banner>,
    )
  }

  if (items.length === 0) return null
  return <div className="banners">{items}</div>
}

function healthProbeTitle(probe: HealthProbe): string {
  const titles: Record<string, string> = {
    clock_drift: 'Дрейф часов',
    exchange_health: 'Состояние бирж',
    liquidation: 'Близко к ликвидации',
    margin: 'Маржа счёта',
    trading_status: 'Торговый статус инструмента',
  }
  return titles[probe.name] ?? probe.name
}
